// src/pages/Brokers.jsx
import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { motion } from "framer-motion";
import { FaMapMarkerAlt, FaPhoneAlt, FaArrowRight, FaSearch } from "react-icons/fa";

import app from "../firebase";
import {
  getFirestore,
  collection,
  query,
  orderBy,
  onSnapshot,
} from "firebase/firestore";

const db = getFirestore(app);

// --- Helpers
const initials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .map((n) => n[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const formatUSPhone = (digits = "") => {
  const d = digits.replace(/\D/g, "").slice(-10);
  if (d.length !== 10) return "";
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

export default function Brokers() {
  // Firestore data
  const [brokers, setBrokers] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  // UI helpers
  const fadeUp = (d = 0) => ({
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.2 },
    transition: { duration: 0.45, delay: d },
  });

  // filters
  const [search, setSearch] = React.useState("");
  const [stateFilter, setStateFilter] = React.useState("All");
  const [sortBy, setSortBy] = React.useState("name-asc");

  // subscribe to brokers (public read)
  React.useEffect(() => {
    const q = query(collection(db, "brokers"), orderBy("name"));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => {
          const v = d.data() || {};
          const city = v.city || "";
          const state = v.state || "";
          const location = [city, state].filter(Boolean).join(", ");
          const phoneDigits = (v.phone || "").toString().replace(/\D/g, "").slice(-10);

          return {
            id: d.id,
            name: v.name || "Broker",
            city,
            state,
            location,
            phoneDigits,
            phoneDisplay: formatUSPhone(phoneDigits) || (v.phone || ""),
            image: v.profilePic || v.profilePicture || v.photoURL || "",
            email: v.email || "",
            zip: v.zip || "",
          };
        });
        setBrokers(list);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, []);

  // state dropdown options from real data
  const states = React.useMemo(() => {
    const s = Array.from(new Set(brokers.map((b) => b.state).filter(Boolean))).sort();
    return ["All", ...s];
  }, [brokers]);

  // filtered/sorted list
  const filtered = React.useMemo(() => {
    let list = brokers.slice();

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((b) => {
        const h = `${b.name} ${b.city} ${b.state} ${b.location} ${b.zip}`.toLowerCase();
        return h.includes(q);
      });
    }

    if (stateFilter !== "All") {
      list = list.filter((b) => b.state === stateFilter);
    }

    if (sortBy === "name-asc") list.sort((a, b) => a.name.localeCompare(b.name));
    if (sortBy === "name-desc") list.sort((a, b) => b.name.localeCompare(a.name));

    return list;
  }, [search, stateFilter, sortBy, brokers]);

  return (
    <div className="overflow-y-auto scrollbar-hide font-sans text-[#002b5c] bg-white">
      <Helmet>
        <title>Brokers | Your Choice Associates</title>
        <meta
          name="description"
          content="Find a local, licensed broker to help you compare Medicare and Life Insurance plans with no-cost guidance."
        />
      </Helmet>

      <Navbar />

      {/* HERO (compact) */}
      <section className="relative">
        <img
          src="/images/hero.png"
          alt="Licensed brokers offering guidance"
          className="w-full h-[360px] md:h-[420px] object-cover object-center"
        />
        <div className="absolute inset-0 bg-[#002b5c]/80" />
        <div className="absolute inset-0 flex items-center">
          <div className="w-full px-6">
            <div className="max-w-5xl mx-auto text-center text-white">
              <motion.h1 {...fadeUp(0)} className="text-4xl md:text-5xl font-extrabold leading-tight">
                Meet Our Licensed Brokers
              </motion.h1>
              <motion.p {...fadeUp(0.12)} className="mt-3 text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
                Friendly experts across the country—ready to help you compare plans and feel confident in your choice.
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* FILTER BAR */}
      <section className="bg-white py-8 px-6 border-b border-[#002b5c]/10">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
            {/* Search */}
            <div className="flex-1">
              <label className="sr-only" htmlFor="search">Search brokers</label>
              <div className="flex items-stretch bg-white rounded-full shadow-sm ring-1 ring-black/5 overflow-hidden">
                <span className="px-4 flex items-center text-gray-500">
                  <FaSearch />
                </span>
                <input
                  id="search"
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name, city, or state (e.g., “Miami” or “AZ”)"
                  className="flex-1 min-w-0 px-2 pr-4 py-3 text-[#002b5c] placeholder:text-[#002b5c]/50 focus:outline-none"
                />
              </div>
            </div>

            {/* State filter + Sort */}
            <div className="flex items-center gap-3">
              <div>
                <label htmlFor="state" className="block text-xs font-semibold text-[#002b5c]/70 mb-1">
                  State
                </label>
                <select
                  id="state"
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  className="rounded-full border border-[#002b5c]/20 px-4 py-2.5 bg-white text-sm"
                >
                  {states.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="sort" className="block text-xs font-semibold text-[#002b5c]/70 mb-1">
                  Sort
                </label>
                <select
                  id="sort"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="rounded-full border border-[#002b5c]/20 px-4 py-2.5 bg-white text-sm"
                >
                  <option value="name-asc">Name A–Z</option>
                  <option value="name-desc">Name Z–A</option>
                </select>
              </div>
            </div>
          </div>

          {/* Results count */}
          <div className="mt-3 text-sm text-gray-600">
            {loading ? (
              <>Loading brokers…</>
            ) : (
              <>
                Showing <span className="font-semibold text-[#002b5c]">{filtered.length}</span>{" "}
                broker{filtered.length === 1 ? "" : "s"}
              </>
            )}
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="bg-white py-20 px-6">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="grid gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="bg-white rounded-2xl border border-[#002b5c]/12 shadow-sm p-7 animate-pulse">
                  <div className="mx-auto mb-4 h-20 w-20 rounded-full bg-gray-200" />
                  <div className="h-4 bg-gray-200 w-1/2 mx-auto mb-2 rounded" />
                  <div className="h-3 bg-gray-200 w-1/3 mx-auto rounded" />
                  <div className="h-px bg-gray-200 my-5" />
                  <div className="h-10 bg-gray-200 rounded-full mb-3" />
                  <div className="h-10 bg-gray-200 rounded-full" />
                </div>
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-gray-600">
              No brokers match your filters. Try clearing search or choosing a different state.
            </div>
          ) : (
            <div className="grid gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((b, idx) => (
                <motion.article
                  key={b.id || `${b.name}-${idx}`}
                  {...fadeUp(0.02 * (idx % 6))}
                  className="bg-white rounded-2xl border border-[#002b5c]/12 shadow-sm hover:shadow-md transition-colors"
                >
                  <div className="p-7 text-center">
                    {b.image ? (
                      <img
                        src={b.image}
                        alt={b.name}
                        className="mx-auto mb-4 h-20 w-20 rounded-full object-cover ring-2 ring-[#002b5c]/15"
                        loading="lazy"
                      />
                    ) : (
                      <div className="mx-auto mb-4 h-20 w-20 rounded-full bg-gradient-to-br from-[#0a2b52] to-[#001b35] text-white flex items-center justify-center text-2xl font-bold ring-2 ring-[#002b5c]/10">
                        {initials(b.name)}
                      </div>
                    )}

                    <h3 className="text-xl font-bold text-[#002b5c] leading-snug">{b.name}</h3>
                    {(b.city || b.state) && (
                      <div className="mt-1 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs bg-[#002b5c]/5 text-[#002b5c] font-medium">
                        <FaMapMarkerAlt className="text-[#e31c3d]" />
                        <span>{[b.city, b.state].filter(Boolean).join(", ")}</span>
                      </div>
                    )}
                  </div>

                  <div className="h-px bg-[#002b5c]/10 mx-7" />

                  <div className="p-7 pt-5 flex flex-col items-stretch gap-3">
                    {b.phoneDigits ? (
                      <a
                        href={`tel:+1${b.phoneDigits}`}
                        aria-label={`Call ${b.name} at ${formatUSPhone(b.phoneDigits)}`}
                        className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#e31c3d]/30 px-5 py-3 text-sm text-[#e31c3d] font-semibold hover:bg-[#e31c3d]/10 transition-colors whitespace-nowrap"
                      >
                        <FaPhoneAlt className="shrink-0" />
                        <span className="whitespace-nowrap">{formatUSPhone(b.phoneDigits)}</span>
                      </a>
                    ) : (
                      <div
                        className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-gray-200 px-5 py-3 text-sm text-gray-500 font-semibold cursor-not-allowed"
                        title="Phone number unavailable"
                      >
                        <FaPhoneAlt className="shrink-0" />
                        <span className="whitespace-nowrap">Phone unavailable</span>
                      </div>
                    )}

                    <Link
                      to="/questionnaire"
                      className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#002b5c] text-white px-5 py-3 text-sm font-semibold hover:bg-[#001b35] transition-colors whitespace-nowrap"
                    >
                      Get Free Advice <FaArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#e31c3d]/5 py-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#002b5c]">
            Prefer to browse plans first?
          </h2>
          <p className="text-gray-700 mt-3">
            Compare Medicare Advantage, Part D, and Medigap options side-by-side in minutes.
          </p>
          <div className="mt-8">
            <Link
              to="/plans"
              className="bg-[#002b5c] text-white px-6 py-3 rounded-full font-semibold shadow-md hover:bg-[#001b35] transition"
            >
              Explore Plans
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
